/*
 * Pinzelli — product-gallery.js
 * Galerie produit "pgc" : piste scroll-snap (mobile + desktop), vignettes rondes
 * en overlay (mobile) ou en colonne (desktop), pastilles de pagination, compteur,
 * et visionneuse plein écran.
 */
if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-pgc-main]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-pgc-slide]'));
      if (!this.track || !this.slides.length) return;

      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-pgc-thumb]'));
      this.dthumbs = Array.prototype.slice.call(this.querySelectorAll('[data-pgc-dthumb]'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-pgc-dot]'));
      this.counter = this.querySelector('[data-pgc-counter]');
      this.zoomEnabled = this.dataset.zoom === 'true';

      this.fs = this.querySelector('[data-pgc-fs]');
      this.fsTrack = this.querySelector('[data-pgc-fs-track]');
      this.fsCounter = this.querySelector('[data-pgc-fs-counter]');
      this.fsClose = this.querySelector('[data-pgc-fs-close]');

      this.total = this.slides.length;
      this.current = Math.max(0, this.slides.findIndex(function (s) { return s.classList.contains('is-active'); }));

      this.bind();
      this.syncUI(this.current);
      if (this.current > 0) {
        var self = this;
        requestAnimationFrame(function () { self.track.scrollLeft = self.current * self.track.clientWidth; });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      if (this.fs && this.fs.classList.contains('is-open')) document.body.style.overflow = '';
    }

    bind() {
      var self = this;

      this.thumbs.concat(this.dthumbs).forEach(function (button) {
        button.addEventListener('click', function () {
          var index = parseInt(button.getAttribute('data-pgc-thumb') || button.getAttribute('data-pgc-dthumb'), 10);
          self.goTo(index, true);
        });
      });

      this.dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          self.goTo(parseInt(dot.getAttribute('data-pgc-dot'), 10), true);
        });
      });

      var timer;
      this.track.addEventListener('scroll', function () {
        clearTimeout(timer);
        timer = setTimeout(function () { self.syncUI(self.trackIndex()); }, 60);
      }, { passive: true });

      if (this.zoomEnabled && this.fs) {
        this.slides.forEach(function (slide, index) {
          slide.addEventListener('click', function () { self.openFullscreen(index); });
        });
        this.querySelectorAll('[data-pgc-more], [data-pgc-dmore]').forEach(function (button) {
          button.addEventListener('click', function () { self.openFullscreen(self.current); });
        });
        if (this.fsClose) this.fsClose.addEventListener('click', function () { self.closeFullscreen(); });
        if (this.fsTrack) {
          var fsTimer;
          this.fsTrack.addEventListener('scroll', function () {
            clearTimeout(fsTimer);
            fsTimer = setTimeout(function () { self.updateFsCounter(); }, 80);
          }, { passive: true });
        }
      }

      this.onKeydown = function (event) {
        if (self.fs && self.fs.classList.contains('is-open')) {
          if (event.key === 'Escape') self.closeFullscreen();
          return;
        }
        if (!self.contains(document.activeElement)) return;
        if (event.key === 'ArrowRight') { event.preventDefault(); self.goTo(self.current + 1, true); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); self.goTo(self.current - 1, true); }
      };
      document.addEventListener('keydown', this.onKeydown);
    }

    trackIndex() {
      var width = this.track.clientWidth || 1;
      return Math.max(0, Math.min(this.total - 1, Math.round(this.track.scrollLeft / width)));
    }

    pad(n) { return (n < 10 ? '0' : '') + n; }

    syncUI(index) {
      if (index < 0 || index >= this.total) return;
      this.current = index;
      this.slides.forEach(function (slide, i) { slide.classList.toggle('is-active', i === index); });
      this.thumbs.forEach(function (t) {
        t.classList.toggle('is-sel', parseInt(t.getAttribute('data-pgc-thumb'), 10) === index);
      });
      this.dthumbs.forEach(function (t) {
        t.classList.toggle('is-sel', parseInt(t.getAttribute('data-pgc-dthumb'), 10) === index);
      });
      this.dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === index);
        d.setAttribute('aria-current', String(i === index));
      });
      if (this.counter) this.counter.textContent = this.pad(index + 1) + ' / ' + this.pad(this.total);
    }

    goTo(index, smooth) {
      if (index < 0 || index >= this.total) return;
      try {
        this.track.scrollTo({ left: index * this.track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      } catch (e) {
        this.track.scrollLeft = index * this.track.clientWidth;
      }
      this.syncUI(index);
    }

    /** API publique : cible le média d'une variante. */
    goToMedia(mediaId) {
      var slide = this.querySelector('[data-pgc-slide][data-media-id="' + mediaId + '"]');
      if (!slide) return false;
      var index = parseInt(slide.getAttribute('data-pgc-slide'), 10);
      if (isNaN(index)) return false;
      this.goTo(index, true);
      return true;
    }

    fsIndex() {
      if (!this.fsTrack) return this.current;
      return Math.round(this.fsTrack.scrollLeft / (this.fsTrack.clientWidth || 1));
    }

    updateFsCounter() {
      if (this.fsCounter) this.fsCounter.textContent = this.pad(this.fsIndex() + 1) + ' / ' + this.pad(this.total);
    }

    openFullscreen(index) {
      if (!this.fs) return;
      this.fs.classList.add('is-open');
      this.fs.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      var self = this;
      requestAnimationFrame(function () {
        self.fsTrack.scrollLeft = index * self.fsTrack.clientWidth;
        self.updateFsCounter();
        if (self.fsClose) self.fsClose.focus();
      });
    }

    closeFullscreen() {
      if (!this.fs) return;
      this.fs.classList.remove('is-open');
      this.fs.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.goTo(this.fsIndex(), false);
    }
  });
}
